import { alpha, styled } from '@mui/material/styles';
import { Dialog, DialogSelectTrigger } from 'components/ui/Dialog';

interface FilterOptionStyleProps {
  isSelected?: boolean;
}

const shouldForwardFilterOptionProp = (prop: PropertyKey) => prop !== 'isSelected';

export const FirmDocumentsFilterDialogRoot = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    minWidth: 420,
    [theme.breakpoints.down('sm')]: {
      minWidth: 'calc(100vw - var(--dialog-viewport-margin) - var(--dialog-viewport-margin))',
    },
  },
}));

export const FirmDocumentsFilterDialogSection = styled('section')(({ theme }) => ({
  display: 'grid',
  gap: theme.spacing(2),
  minWidth: 0,
  '& + &': {
    borderTop: `1px solid ${theme.palette.neutral[200]}`,
    paddingTop: theme.spacing(2.5),
  },
}));

export const FirmDocumentsFilterDialogSectionTitle = styled('h3')(({ theme }) => ({
  alignItems: 'center',
  color: theme.palette.brand[700],
  display: 'inline-flex',
  fontSize: 13,
  fontWeight: 600,
  gap: theme.spacing(0.75),
  lineHeight: theme.typography.body2.lineHeight,
  margin: 0,
}));

export const FirmDocumentsFilterDialogSectionTitleIcon = styled('span')(({ theme }) => ({
  alignItems: 'center',
  color: theme.palette.brand[500],
  display: 'inline-flex',
  flex: '0 0 16px',
  height: 16,
  justifyContent: 'center',
  width: 16,
  '& .MuiSvgIcon-root': {
    fontSize: 16,
  },
}));

export const FirmDocumentsFilterDialogFilterFields = styled('div')(({ theme }) => ({
  display: 'grid',
  gap: theme.spacing(2),
  minWidth: 0,
}));

export const FirmDocumentsFilterDialogField = styled('div')(({ theme }) => ({
  display: 'grid',
  gap: theme.spacing(1),
  minWidth: 0,
}));

export const FirmDocumentsFilterDialogFieldLabel = styled('span')(({ theme }) => ({
  color: theme.palette.neutral[600],
  fontFamily: theme.typography.fontFamily,
  fontSize: 12,
  fontWeight: 500,
  lineHeight: '16px',
}));

export const FirmDocumentsFilterDialogSelectTrigger = styled(DialogSelectTrigger)(({ theme }) => ({
  borderColor: theme.palette.neutral[200],
  fontSize: 12,
  height: 40,
  lineHeight: '16px',
  padding: `0 ${theme.spacing(1.5)}`,
}));

export const FirmDocumentsFilterDialogOptions = styled('div')(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  minWidth: 0,
}));

export const FirmDocumentsFilterDialogSortOptions = styled('div')(({ theme }) => ({
  display: 'grid',
  gap: theme.spacing(1),
  minWidth: 0,
}));

export const FirmDocumentsFilterDialogSortOption = styled('div', {
  shouldForwardProp: shouldForwardFilterOptionProp,
})<FilterOptionStyleProps>(({ isSelected = false, theme }) => ({
  alignItems: 'center',
  color: isSelected ? theme.palette.brand[800] : theme.palette.neutral[600],
  display: 'flex',
  gap: theme.spacing(1),
  minWidth: 0,
  width: '100%',
}));

export const FirmDocumentsFilterDialogSortOptionButton = styled('button', {
  shouldForwardProp: shouldForwardFilterOptionProp,
})<FilterOptionStyleProps>(({ isSelected = false, theme }) => ({
  alignItems: 'center',
  backgroundColor: isSelected ? theme.palette.brand[100] : 'transparent',
  border: 0,
  borderRadius: theme.shape.radius.xs,
  boxShadow: isSelected ? `inset 0 0 0 1px ${theme.palette.brand[200]}` : 'none',
  color: 'inherit',
  cursor: 'pointer',
  display: 'flex',
  flex: '1 1 auto',
  gap: theme.spacing(1),
  minHeight: 24,
  minWidth: 0,
  padding: `${theme.spacing(0.5)} ${theme.spacing(0.75)}`,
  textAlign: 'left',
  transition: theme.transitions.create(['background-color', 'box-shadow', 'color'], {
    duration: theme.transitions.duration.shortest,
  }),
  width: '100%',
  '&:hover, &:focus-visible': {
    backgroundColor: isSelected ? theme.palette.brand[100] : theme.palette.neutral[50],
    boxShadow: `inset 0 0 0 1px ${isSelected ? theme.palette.brand[400] : theme.palette.neutral[200]}`,
    color: isSelected ? theme.palette.brand[800] : theme.palette.neutral[700],
    outline: 0,
  },
}));

export const FirmDocumentsFilterDialogSortControl = styled('span', {
  shouldForwardProp: shouldForwardFilterOptionProp,
})<FilterOptionStyleProps>(({ isSelected = false, theme }) => ({
  alignItems: 'center',
  backgroundColor: isSelected ? theme.palette.brand[500] : theme.palette.neutral.white,
  border: `1px solid ${isSelected ? theme.palette.brand[500] : theme.palette.neutral[500]}`,
  borderRadius: theme.shape.radius.xs,
  boxSizing: 'border-box',
  color: theme.palette.neutral.white,
  display: 'inline-flex',
  flex: '0 0 16px',
  height: 16,
  justifyContent: 'center',
  transition: theme.transitions.create(['background-color', 'border-color'], {
    duration: theme.transitions.duration.shortest,
  }),
  width: 16,
  '&::after': {
    borderBottom: isSelected ? '2px solid currentColor' : 0,
    borderLeft: isSelected ? '2px solid currentColor' : 0,
    boxSizing: 'border-box',
    content: '""',
    height: 5,
    transform: 'rotate(-45deg) translateY(-1px)',
    width: 9,
  },
}));

export const FirmDocumentsFilterDialogSortOptionLabel = styled('span')(({ theme }) => ({
  color: 'inherit',
  flex: '1 1 auto',
  fontFamily: theme.typography.fontFamily,
  fontSize: 12,
  fontWeight: theme.typography.fontWeightRegular,
  lineHeight: '16px',
  minWidth: 0,
}));

export const FirmDocumentsFilterDialogSortOptionMutedText = styled('span')(({ theme }) => ({
  color: theme.palette.neutral[500],
}));

export const FirmDocumentsFilterDialogSortDirectionButton = styled('button')(({ theme }) => ({
  alignItems: 'center',
  appearance: 'none',
  backgroundColor: theme.palette.neutral.white,
  border: `1px solid ${theme.palette.brand[200]}`,
  borderRadius: theme.shape.radius.xs,
  color: theme.palette.brand[700],
  cursor: 'pointer',
  display: 'inline-flex',
  flex: '0 0 24px',
  height: 24,
  justifyContent: 'center',
  padding: 0,
  transition: theme.transitions.create(['background-color', 'border-color', 'color'], {
    duration: theme.transitions.duration.shortest,
  }),
  width: 24,
  '&:hover, &:focus-visible': {
    backgroundColor: theme.palette.brand[100],
    borderColor: theme.palette.brand[400],
    color: theme.palette.brand[800],
    outline: 0,
  },
  '& .MuiSvgIcon-root': {
    fontSize: 16,
  },
}));

export const FirmDocumentsFilterDialogFileTypeOptionContent = styled('span')(({ theme }) => ({
  alignItems: 'center',
  display: 'inline-flex',
  gap: theme.spacing(0.5),
  minWidth: 0,
}));

export const FirmDocumentsFilterDialogFileTypeOptionIcon = styled('span')(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.palette.neutral[100],
  borderRadius: theme.shape.radius.xs,
  boxShadow: `inset 0 0 0 1px ${theme.palette.neutral[100]}`,
  boxSizing: 'border-box',
  display: 'inline-flex',
  flex: '0 0 18px',
  fontSize: 14,
  height: 18,
  justifyContent: 'center',
  transition: theme.transitions.create(['background-color', 'box-shadow'], {
    duration: theme.transitions.duration.shortest,
  }),
  width: 18,
  '& .MuiSvgIcon-root': {
    fontSize: 14,
  },
}));

export const FirmDocumentsFilterDialogOption = styled('button', {
  shouldForwardProp: shouldForwardFilterOptionProp,
})<FilterOptionStyleProps>(({ isSelected = false, theme }) => ({
  alignItems: 'center',
  backgroundColor: isSelected ? theme.palette.brand[300] : theme.palette.neutral[100],
  border: 0,
  borderRadius: theme.shape.radius.xs,
  color: isSelected ? theme.palette.neutral.white : theme.palette.neutral[600],
  cursor: 'pointer',
  display: 'inline-flex',
  fontFamily: theme.typography.fontFamily,
  fontSize: 12,
  fontWeight: theme.typography.fontWeightRegular,
  lineHeight: '16px',
  minHeight: 24,
  padding: `${theme.spacing(0.5)} ${theme.spacing(0.75)}`,
  transition: theme.transitions.create(['background-color', 'color'], {
    duration: theme.transitions.duration.shortest,
  }),
  whiteSpace: 'nowrap',
  [`& ${FirmDocumentsFilterDialogFileTypeOptionIcon}`]: {
    backgroundColor: isSelected ? theme.palette.neutral.white : theme.palette.neutral[100],
    boxShadow: `inset 0 0 0 1px ${isSelected
      ? alpha(theme.palette.neutral[900], 0.12)
      : theme.palette.neutral[100]}`,
  },
  '&:hover, &:focus-visible': {
    backgroundColor: isSelected ? theme.palette.brand[400] : theme.palette.neutral[200],
    color: isSelected ? theme.palette.neutral.white : theme.palette.neutral[700],
    outline: 0,
    [`& ${FirmDocumentsFilterDialogFileTypeOptionIcon}`]: {
      backgroundColor: isSelected ? theme.palette.neutral.white : theme.palette.neutral[200],
      boxShadow: `inset 0 0 0 1px ${isSelected
        ? alpha(theme.palette.neutral[900], 0.14)
        : theme.palette.neutral[200]}`,
    },
  },
}));
